<template>
  <div class="main-bg">
    <!-- 顶部导航栏 -->
    <div class="navbar">
      <div class="restaurant-selector">
        <select
          v-model="selectedShopId"
          class="restaurant-dropdown"
          disabled
          @change="onShopChange"
        >
          <option v-if="shopList.length === 0" disabled>加载中...</option>
          <option v-for="shop in shopList" :key="shop.shopId" :value="shop.shopId">
            {{ shop.shopName }}
          </option>
        </select>
      </div>
      <div class="flex items-center">
        <!-- 登录工号 -->
        <div class="flex items-center mr-4">
          <span v-if="currentEmployeeId" class="text-white mr-2 text-lg">
            工号: {{ currentEmployeeId }}
          </span>
        </div>

        <!-- 导航菜单 -->
        <div class="nav-dropdown mr-3">
          <button class="nav-menu-btn" @click="toggleNavMenu">
            <i class="fas fa-bars"></i> 菜单
            <i class="fas fa-chevron-down ml-1"></i>
          </button>
          <div class="nav-menu" :class="{ show: showNavMenu }">
            <button class="nav-menu-item" @click="goToTableManage">
              <i class="fas fa-table"></i> 桌位管理
            </button>
            <button class="nav-menu-item" @click="goToKitchen">
              <i class="fas fa-utensils"></i> 订单出餐
            </button>
            <button class="nav-menu-item" @click="goToOrderHistory">
              <i class="fas fa-receipt"></i> 订单查询
            </button>
            <button class="nav-menu-item" @click="goToDishAssignment">
              <i class="fas fa-tasks"></i> 菜品分配
            </button>
            <button class="nav-menu-item" @click="goToHistory">
              <i class="fas fa-history"></i> 出餐历史
            </button>
            <button class="nav-menu-item" @click="goToFoodManage">
              <i class="fas fa-utensils"></i> 菜品管理
            </button>
          </div>
        </div>

        <button class="text-white mr-4" @click="toggleLanguage">
          <i class="fas fa-language text-2xl"></i>
        </button>
        <button class="mr-4">
          <i class="fas fa-search text-white text-xl"></i>
        </button>
        <button>
          <i class="fas fa-bell text-white text-xl"></i>
        </button>
      </div>
    </div>
    <!-- 刷新桌位状态按钮 -->
    <div class="refresh-bar">
      <button @click="refreshTableStatus">点击此处【刷新】点餐状态</button>
    </div>
    <!-- 桌位状态图例 -->
    <div class="legend-bar">
      <div class="legend-list">
        <div class="legend-item">
          <div class="legend-dot legend-dot-blue"></div>
          <span>尚未点餐</span>
        </div>
        <div class="legend-item">
          <div class="legend-dot legend-dot-green"></div>
          <span>已点餐</span>
        </div>
        <div class="legend-item">
          <div class="legend-dot legend-dot-yellow"></div>
          <span>已预订</span>
        </div>
        <div class="legend-item">
          <div class="legend-dot legend-dot-gray"></div>
          <span>已禁用</span>
        </div>
        <div class="legend-item">
          <div class="legend-dot legend-dot-pink"></div>
          <span>清理中</span>
        </div>
      </div>
      <div class="legend-time">
        <i class="fas fa-clock mr-2"></i>
        <span>{{ lastRefreshTime }}</span>
      </div>
    </div>
    <!-- 桌位网格 -->
    <div class="table-grid-wrap">
      <div class="table-grid">
        <!-- 动态桌位卡片 -->
        <div
          v-for="table in orderTableList"
          :key="table.tableId"
          class="table-card glass-card"
          :class="getTableStatusClass(table.tableStatus)"
          @click="handleTableClick(table)"
        >
          <!-- 确认订单按钮 -->
          <button
            v-if="shouldShowConfirmButton(table)"
            class="confirm-order-btn"
            title="确认订单"
            @click.stop="confirmOrder(table)"
          >
            <i class="fas fa-check"></i>
          </button>

          <!-- 翻台按钮 -->
          <button
            v-if="shouldShowFlipButton(table)"
            class="flip-table-btn"
            title="翻台"
            @click.stop="flipTable(table)"
          >
            <i class="fas fa-sync-alt"></i>
          </button>

          <div class="table-title">{{ table.tableName }}</div>
          <div class="table-status">
            <span class="status-badge" :class="{ 'text-red-600': table.tableStatus === 1 }">
              <i :class="getStatusIcon(table.tableStatus)"></i>
              {{ table.tableStatusName || getStatusText(table.tableStatus) }}
            </span>
          </div>

          <!-- 详细信息 -->
          <div class="table-info">
            <!-- 基础信息 -->
            <div v-if="table.tableStatus === 0" class="table-info-row">
              <i class="fas fa-users text-gray-500"></i>
              <span>{{ table.seatsDesc || (table.seats ? table.seats + '人桌' : '2-4人桌') }}</span>
            </div>
            <div v-if="table.tableStatus === 0" class="table-info-row">
              <i class="fas fa-clock text-gray-500"></i>
              <span>空闲中</span>
            </div>

            <!-- 已点餐/用餐中状态 -->
            <div v-if="isOccupiedTable(table)" class="occupied-info">
              <div class="table-info-row-occupied">
                <div class="flex items-center">
                  <i class="fas fa-users text-green-600"></i>
                  <span>{{ getPersonCount(table) }}人用餐</span>
                </div>
              </div>

              <div class="table-info-row-occupied">
                <div class="flex items-center">
                  <i class="fas fa-clock text-green-600"></i>
                  <span>{{ getOpenTime(table) }}</span>
                </div>
              </div>

              <div class="table-info-row-occupied text-xs">
                <div class="flex items-center">
                  <i :class="getPaymentStatusIcon(table)"></i>
                  <span :class="getPaymentStatusClass(table)">{{
                    getPaymentStatusText(table)
                  }}</span>
                </div>
                <div class="amount-text text-green-600">¥{{ getTotalAmount(table) }}</div>
              </div>
            </div>

            <!-- 已预订状态 -->
            <div v-if="table.tableStatus === 10" class="reserved-info">
              <div class="table-info-row-occupied">
                <div class="flex items-center">
                  <i class="fas fa-users text-yellow-600"></i>
                  <span>6人预订</span>
                </div>
                <div class="text-yellow-600 font-semibold">18:30</div>
              </div>
              <div class="table-info-row-occupied">
                <div class="flex items-center text-xs">
                  <i class="fas fa-user text-yellow-600"></i>
                  <span>李先生</span>
                </div>
                <div class="flex items-center text-xs">
                  <i class="fas fa-phone text-gray-500"></i>
                  <span>138****8888</span>
                </div>
              </div>
              <div class="flex items-center text-xs">
                <i class="fas fa-clock text-orange-500"></i>
                <span>2小时后到达</span>
              </div>
            </div>

            <!-- 已禁用状态 -->
            <div v-if="table.tableStatus === 3" class="disabled-info">
              <div class="table-info-row">
                <i class="fas fa-exclamation-triangle text-red-500"></i>
                <span>桌椅维修中</span>
              </div>
              <div class="flex items-center text-xs">
                <i class="fas fa-clock text-gray-500"></i>
                <span>预计明日可用</span>
              </div>
            </div>

            <!-- 清理中状态 -->
            <div v-if="table.tableStatus === 4" class="cleaning-info">
              <div class="table-info-row">
                <i class="fas fa-user-tie text-pink-600"></i>
                <span>王清洁员</span>
              </div>
              <div class="flex items-center justify-between">
                <div class="flex items-center text-xs">
                  <i class="fas fa-clock text-pink-500"></i>
                  <span>15:30开始</span>
                </div>
                <div class="text-pink-600 font-semibold text-xs">10分钟后完成</div>
              </div>
            </div>

            <!-- 订单号 -->
            <!-- <div v-if="table.orderNo" class="table-info-row">
              <i class="fas fa-receipt"></i>
              <span>订单: {{ table.orderNo }}</span>
            </div> -->
          </div>
        </div>
        <!-- 添加座位按钮 -->
        <div class="add-table-card glass-card" @click="handleAddTable">
          <div class="add-table-content">
            <i class="fas fa-plus"></i>
            <div>添加座位</div>
          </div>
        </div>
      </div>
    </div>

    <!-- 翻台进度模态框 -->
    <div v-if="showFlipProgress" class="modal-overlay">
      <div class="modal-content">
        <div class="text-center">
          <div class="mb-4">
            <i class="fas fa-sync-alt text-4xl text-blue-500 animate-spin"></i>
          </div>
          <h3 class="text-xl font-semibold mb-2">{{ currentFlipTable?.tableName }}翻台中...</h3>
          <p class="text-gray-600 mb-4">正在清理桌面，请稍候</p>
          <div class="progress-bar-container">
            <div class="progress-bar-fill" :style="{ width: flipProgress + '%' }"></div>
          </div>
          <p class="text-sm text-gray-500 mt-2">{{ flipProgressText }}</p>
        </div>
      </div>
    </div>

    <!-- 确认订单进度模态框 -->
    <div v-if="showConfirmProgress" class="modal-overlay">
      <div class="modal-content">
        <div class="text-center">
          <div class="mb-4">
            <i class="fas fa-check-circle text-4xl text-green-500 animate-pulse"></i>
          </div>
          <h3 class="text-xl font-semibold mb-2"
            >{{ currentConfirmTable?.tableName }}订单确认中...</h3
          >
          <p class="text-gray-600 mb-4">正在发送至厨房系统</p>
          <div class="progress-bar-container">
            <div
              class="progress-bar-fill bg-green-500"
              :style="{ width: confirmProgress + '%' }"
            ></div>
          </div>
          <p class="text-sm text-gray-500 mt-2">{{ confirmProgressText }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { onMounted, ref, onUnmounted } from 'vue';
  import { fetchShopList } from '@/service/base';
  import { getOrderTableList, auditOrder, completeOrder } from '@/service/order';
  import { OrderTableListItemModel } from '@/model/order';
  import { useRouter } from 'vue-router';
  import { useCurrentEmployee } from '@/composables/useCurrentEmployee';
  const router = useRouter();
  const { employeeId: currentEmployeeId } = useCurrentEmployee();

  const shopList = ref<any[]>([]);
  const selectedShopId = ref<number | null>(null);
  const orderTableList = ref<Array<OrderTableListItemModel>>([]);
  const isTableNavigationPending = ref(false);
  let latestTableListRequestId = 0;

  // localStorage key for selected shop
  const SELECTED_SHOP_KEY = 'table_manage_selected_shop';

  // 保存选中的店铺到localStorage
  const saveSelectedShop = (shopId: number) => {
    try {
      localStorage.setItem(SELECTED_SHOP_KEY, shopId.toString());
    } catch (e) {
      console.error('保存选中店铺失败:', e);
    }
  };

  // 从localStorage读取选中的店铺
  const loadSelectedShop = () => {
    try {
      return localStorage.getItem(SELECTED_SHOP_KEY);
    } catch (e) {
      console.error('读取选中店铺失败:', e);
      return null;
    }
  };

  // 翻台相关状态
  const showFlipProgress = ref(false);
  const currentFlipTable = ref<OrderTableListItemModel | null>(null);
  const flipProgress = ref(0);
  const flipProgressText = ref('');

  // 确认订单相关状态
  const showConfirmProgress = ref(false);
  const currentConfirmTable = ref<OrderTableListItemModel | null>(null);
  const confirmProgress = ref(0);
  const confirmProgressText = ref('');

  // 导航菜单状态
  const showNavMenu = ref(false);

  // 定时器相关状态
  const lastRefreshTime = ref('');
  let autoRefreshTimer: NodeJS.Timeout | null = null;

  // 获取桌位列表
  const fetchOrderTableList = async (shopId: number) => {
    const requestId = ++latestTableListRequestId;
    const respOrderTables = await getOrderTableList({
      pageNum: 1,
      pageSize: 999,
      data: {
        shopId: shopId,
      },
    });
    if (
      respOrderTables.code === 200 &&
      requestId === latestTableListRequestId &&
      selectedShopId.value === shopId
    ) {
      orderTableList.value = respOrderTables.result.list;
    }
  };

  const onShopChange = async () => {
    if (selectedShopId.value !== null) {
      // 保存选中的店铺到localStorage
      saveSelectedShop(selectedShopId.value);
      // 切换店铺后立即清空旧桌位，避免新请求返回前误点旧店铺桌位
      orderTableList.value = [];
      await fetchOrderTableList(selectedShopId.value as number);
    }
  };

  const refreshTableStatus = async () => {
    if (selectedShopId.value !== null) {
      await fetchOrderTableList(selectedShopId.value as number);
      // 更新刷新时间
      updateRefreshTime();
      showToast('刷新成功');
    }
  };

  // 根据 shopId 获取店铺名称
  const getShopNameById = (id?: number) => {
    if (!id) return '';
    const shop = shopList.value.find((s) => s.shopId === id);
    return shop?.shopName || '';
  };

  // 更新刷新时间
  const updateRefreshTime = () => {
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    lastRefreshTime.value = `${hours}:${minutes}:${seconds}`;
  };

  // 启动自动刷新定时器
  const startAutoRefresh = () => {
    // 清除现有定时器
    if (autoRefreshTimer) {
      clearInterval(autoRefreshTimer);
    }

    // 设置10秒定时器
    autoRefreshTimer = setInterval(async () => {
      if (selectedShopId.value !== null) {
        await fetchOrderTableList(selectedShopId.value as number);
        updateRefreshTime();
        console.log('自动刷新完成:', lastRefreshTime.value);
      }
    }, 10000); // 10秒
  };

  // 停止自动刷新定时器
  const stopAutoRefresh = () => {
    if (autoRefreshTimer) {
      clearInterval(autoRefreshTimer);
      autoRefreshTimer = null;
    }
  };

  // 点击外部关闭菜单
  const handleClickOutside = (event: Event) => {
    const target = event.target as HTMLElement;
    if (!target.closest('.nav-dropdown')) {
      showNavMenu.value = false;
    }
  };

  onMounted(async () => {
    const respShop = await fetchShopList({ pageNum: 1, pageSize: 100, data: { shopStatus: 0 } });
    if (respShop.code === 200 && respShop.result.list.length > 0) {
      shopList.value = respShop.result.list;

      // 尝试从localStorage恢复店铺选择
      const savedShopId = loadSelectedShop();
      if (savedShopId && shopList.value.some((shop) => shop.shopId.toString() === savedShopId)) {
        selectedShopId.value = parseInt(savedShopId);
      } else {
        // 如果没有保存的店铺或保存的店铺不存在，使用第一个店铺
        selectedShopId.value = shopList.value[0].shopId;
      }

      await fetchOrderTableList(selectedShopId.value as number);

      // 初始化刷新时间并启动自动刷新
      updateRefreshTime();
      startAutoRefresh();
    }

    // 添加点击外部关闭菜单的事件监听
    document.addEventListener('click', handleClickOutside);
  });

  // 组件卸载时清理定时器和事件监听
  onUnmounted(() => {
    stopAutoRefresh();
    document.removeEventListener('click', handleClickOutside);
  });

  const toggleNavMenu = () => {
    showNavMenu.value = !showNavMenu.value;
  };

  const goToDishAssignment = () => {
    router.push('/dishAssignment');
    showNavMenu.value = false; // 关闭菜单
  };

  const goToKitchen = () => {
    router.push('/kitchenOrder');
    showNavMenu.value = false; // 关闭菜单
  };

  const goToHistory = () => {
    router.push('/servingHistory');
    showNavMenu.value = false; // 关闭菜单
  };

  const goToOrderHistory = () => {
    router.push('/orderHistory');
    showNavMenu.value = false; // 关闭菜单
  };

  const goToTableManage = () => {
    router.push('/tableManage');
    showNavMenu.value = false; // 关闭菜单
  };

  const goToFoodManage = () => {
    router.push('/foodManage');
    showNavMenu.value = false; // 关闭菜单
  };

  const toggleLanguage = () => {
    showToast('语言切换功能将在下一版本推出');
  };

  // 获取桌位状态对应的CSS类
  const getTableStatusClass = (tableStatus: number | undefined) => {
    switch (tableStatus) {
      case 0: // 尚未点餐
        return 'available';
      case 1: // 已点餐
      case 2:
      case 8:
        return 'ordered';
      case 3: // 已禁用
        return 'disabled';
      case 4: // 清理中
        return 'cleaning';
      case 5: // 待确认
        return 'occupied';
      case 6: // 用餐中
        return 'occupied';
      case 10: // 已预订
        return 'reserved';
      default:
        return 'available';
    }
  };

  // 获取状态图标
  const getStatusIcon = (tableStatus: number | undefined) => {
    switch (tableStatus) {
      case 0: // 尚未点餐
        return 'fas fa-utensils text-blue-500 mr-1';
      case 1: // 已点餐
      case 2:
      case 8:
        return 'fas fa-check-circle text-green-500 mr-1';
      case 10: // 已预订
        return 'fas fa-calendar-alt text-yellow-500 mr-1';
      case 3: // 已禁用
        return 'fas fa-ban text-gray-500 mr-1';
      case 4: // 清理中
        return 'fas fa-broom text-pink-500 mr-1';
      case 5: // 待确认
        return 'fas fa-clock text-orange-500 mr-1';
      default:
        return 'fas fa-utensils text-blue-500 mr-1';
    }
  };

  // 获取状态文本
  const getStatusText = (tableStatus: number | undefined) => {
    switch (tableStatus) {
      case 0:
        return '尚未点餐';
      case 1:
        return '已点餐';
      case 10:
        return '已预订';
      case 3:
        return '已禁用';
      case 4:
        return '清理中';
      case 5: // 待确认
        return '待确认';
      case 6: // 用餐中
        return '用餐中';
      case 8:
        return '已出餐';
      default:
        return '未知状态';
    }
  };

  // 判断是否是已占用桌位（已点餐、用餐中等）
  const isOccupiedTable = (table: OrderTableListItemModel) => {
    return table.tableStatus && [1, 2, 5, 6, 8].includes(table.tableStatus);
  };

  // 获取用餐人数
  const getPersonCount = (table: OrderTableListItemModel) => {
    if (table.orderSelectResp) {
      return table.orderSelectResp.personCount || 4;
    }
    return 4;
  };

  // 获取订单总金额
  const getTotalAmount = (table: OrderTableListItemModel) => {
    if (table.orderSelectResp) {
      return table.orderSelectResp.totalAmount || 0;
    }
    return 0;
  };

  // 获取开台时间
  const getOpenTime = (table: OrderTableListItemModel) => {
    if (table.orderSelectResp && table.orderSelectResp.openTime) {
      const openTime = new Date(table.orderSelectResp.openTime);
      const hours = String(openTime.getHours()).padStart(2, '0');
      const minutes = String(openTime.getMinutes()).padStart(2, '0');
      return `${hours}:${minutes}开台`;
    }
    return '刚刚开台';
  };

  // 获取订单状态图标
  const getOrderStatusIcon = (table: OrderTableListItemModel) => {
    if (table.tableStatus === 5) {
      return 'fas fa-hourglass-half text-orange-500 mr-1';
    } else if (table.tableStatus === 1 || table.tableStatus === 2) {
      return 'fas fa-utensils text-blue-500 mr-1';
    } else if (table.tableStatus === 8) {
      return 'fas fa-utensils text-green-500 mr-1';
    }
    return 'fas fa-clock text-blue-500 mr-1';
  };

  // 获取订单状态文本
  const getOrderStatusText = (table: OrderTableListItemModel) => {
    if (table.tableStatus === 5) {
      return '等待确认';
    } else if (table.tableStatus === 1 || table.tableStatus === 2) {
      return '制作中';
    } else if (table.tableStatus === 8) {
      return '已上菜完成';
    }
    return '制作中';
  };

  // 获取支付状态图标
  const getPaymentStatusIcon = (table: OrderTableListItemModel) => {
    const paymentStatus = table.orderSelectResp?.paymentStatusName?.toLowerCase() || '';

    // 已支付状态显示绿色图标
    if (
      paymentStatus.includes('已支付') ||
      paymentStatus.includes('支付成功') ||
      paymentStatus.includes('paid') ||
      paymentStatus.includes('success')
    ) {
      return 'fas fa-check-circle text-green-500 mr-1';
    }

    // 待支付或部分支付显示红色图标
    if (
      paymentStatus.includes('待支付') ||
      paymentStatus.includes('部分支付') ||
      paymentStatus.includes('未支付') ||
      paymentStatus.includes('unpaid') ||
      paymentStatus.includes('partial')
    ) {
      return 'fas fa-credit-card text-red-500 mr-1';
    }

    // 默认显示橙色图标
    return 'fas fa-credit-card text-orange-500 mr-1';
  };

  // 获取支付状态文本
  const getPaymentStatusText = (table: OrderTableListItemModel) => {
    return table.orderSelectResp?.paymentStatusName || '未知状态';
  };

  // 获取支付状态样式类
  const getPaymentStatusClass = (table: OrderTableListItemModel) => {
    const paymentStatus = table.orderSelectResp?.paymentStatusName?.toLowerCase() || '';

    // 已支付状态显示绿色文字
    if (
      paymentStatus.includes('已支付') ||
      paymentStatus.includes('支付成功') ||
      paymentStatus.includes('paid') ||
      paymentStatus.includes('success')
    ) {
      return 'text-green-600 font-semibold';
    }

    // 待支付或部分支付显示红色文字
    if (
      paymentStatus.includes('待支付') ||
      paymentStatus.includes('部分支付') ||
      paymentStatus.includes('未支付') ||
      paymentStatus.includes('unpaid') ||
      paymentStatus.includes('partial')
    ) {
      return 'text-red-600 font-semibold';
    }

    // 默认显示橙色文字
    return 'text-orange-600 font-semibold';
  };

  // 获取桌位状态图标
  const getTableStatusIcon = (table: OrderTableListItemModel) => {
    if (table.tableStatus === 5) {
      return 'fas fa-exclamation-circle text-orange-500 mr-1';
    } else if (table.tableStatus === 8) {
      return 'fas fa-smile text-green-500 mr-1';
    }
    return 'fas fa-clock text-blue-500 mr-1';
  };

  // 获取桌位状态详细文本
  const getTableStatusDetailText = (table: OrderTableListItemModel) => {
    if (table.tableStatus === 5) {
      return '需要确认';
    } else if (table.tableStatus === 8) {
      return '用餐结束';
    }
    return '制作中';
  };

  // 获取桌位状态文本样式类
  const getTableStatusTextClass = (table: OrderTableListItemModel) => {
    if (table.tableStatus === 5) {
      return 'text-orange-600';
    } else if (table.tableStatus === 8) {
      return 'text-green-600';
    }
    return 'text-blue-600';
  };

  // 判断是否显示确认按钮
  const shouldShowConfirmButton = (table: OrderTableListItemModel) => {
    return table.tableStatus === 5; // 待确认状态显示确认按钮
  };

  // 判断是否显示翻台按钮
  const shouldShowFlipButton = (table: OrderTableListItemModel) => {
    return table.tableStatus === 8; // 已完成状态显示翻台按钮
  };

  // 确认订单功能
  const confirmOrder = async (table: OrderTableListItemModel) => {
    if (!confirm(`确定要确认 ${table.tableName} 的订单吗？\n\n确认后订单将发送至厨房开始制作。`)) {
      return;
    }

    currentConfirmTable.value = table;
    showConfirmProgress.value = true;
    confirmProgress.value = 0;
    confirmProgressText.value = '备餐中...';

    // 模拟确认进度
    const steps = [
      { progress: 30, text: '验证订单信息...' },
      { progress: 60, text: '发送至厨房系统...' },
      { progress: 90, text: '更新订单状态...' },
      { progress: 100, text: '确认完成！' },
    ];

    let currentStep = 0;
    const interval = setInterval(async () => {
      if (currentStep < steps.length) {
        confirmProgress.value = steps[currentStep].progress;
        confirmProgressText.value = steps[currentStep].text;

        // 在最后一步调用确认接口
        if (currentStep === steps.length - 1 && table.orderId) {
          try {
            const response = await auditOrder({ orderId: table.orderId });
            if (response.code === 200) {
              showToast(`${table.tableName}订单已确认，已发送至厨房！`);
              // 刷新桌位状态
              if (selectedShopId.value) {
                await fetchOrderTableList(selectedShopId.value);
              }
            } else {
              showToast(response.msg || '确认失败');
            }
          } catch (error) {
            console.error('确认订单失败:', error);
            showToast('确认订单失败，请稍后重试');
          }
        }

        currentStep++;
      } else {
        clearInterval(interval);
        setTimeout(() => {
          showConfirmProgress.value = false;
          currentConfirmTable.value = null;
        }, 500);
      }
    }, 500);
  };

  // 翻台功能
  const flipTable = async (table: OrderTableListItemModel) => {
    if (
      !confirm(`确定要翻台 ${table.tableName} 吗？\n\n翻台后将清理桌面、重置状态，等待下一批客人。`)
    ) {
      return;
    }

    if (!table.orderId) {
      showToast('订单信息不完整，无法翻台');
      return;
    }

    currentFlipTable.value = table;
    showFlipProgress.value = true;
    flipProgress.value = 0;
    flipProgressText.value = '清理中...';

    // 模拟翻台进度
    const steps = [
      { progress: 30, text: '清理餐具...' },
      { progress: 60, text: '消毒桌面...' },
      { progress: 90, text: '整理桌椅...' },
      { progress: 100, text: '翻台完成！' },
    ];

    let currentStep = 0;
    const interval = setInterval(async () => {
      if (currentStep < steps.length) {
        flipProgress.value = steps[currentStep].progress;
        flipProgressText.value = steps[currentStep].text;

        // 在最后一步调用翻台接口
        if (currentStep === steps.length - 1) {
          try {
            const response = await completeOrder({ orderId: table.orderId });
            if (response.code === 200) {
              showToast(`${table.tableName}翻台完成，已准备就绪！`);
              // 刷新桌位状态
              if (selectedShopId.value) {
                await fetchOrderTableList(selectedShopId.value);
              }
            } else {
              showToast(response.msg || '翻台失败');
            }
          } catch (error) {
            console.error('翻台失败:', error);
            showToast('翻台失败，请稍后重试');
          }
        }

        currentStep++;
      } else {
        clearInterval(interval);
        setTimeout(() => {
          showFlipProgress.value = false;
          currentFlipTable.value = null;
        }, 500);
      }
    }, 800);
  };

  const navigateToNewOrder = async (table: OrderTableListItemModel) => {
    if (isTableNavigationPending.value) return;

    if (!table.tableId || !table.shopId || !table.tableName) {
      showToast('桌位信息不完整，请刷新后重试');
      return;
    }

    if (selectedShopId.value !== table.shopId) {
      showToast('桌位信息已更新，请重新选择');
      orderTableList.value = [];
      if (selectedShopId.value !== null) {
        await fetchOrderTableList(selectedShopId.value);
      }
      return;
    }

    isTableNavigationPending.value = true;
    try {
      await router.push({
        path: '/orderNew',
        query: {
          tableNumber: table.tableId,
          tableName: table.tableName,
          tableType: table.tableType,
          tableTag: table.tableTag || '',
          shopId: table.shopId,
          shopName: getShopNameById(table.shopId),
          cartType: table.cartType || 1,
        },
      });
    } catch (error) {
      console.error('进入点餐页面失败:', error);
      showToast('进入点餐页面失败，请重试');
    } finally {
      isTableNavigationPending.value = false;
    }
  };

  // 点击桌位卡片事件
  const handleTableClick = (table: OrderTableListItemModel) => {
    if (isTableNavigationPending.value) return;

    // 如果是禁用或清理中的桌位，显示提示
    if (table.tableStatus === 3) {
      showToast('此桌位暂不可用');
      return;
    }
    if (table.tableStatus === 4) {
      showToast('此桌位正在清理中');
      return;
    }

    if (table.tableStatus === 0) {
      // 未点餐状态，跳转到点餐页面
      void navigateToNewOrder(table);
    } else if (table.tableStatus === 10) {
      // 已预订状态，确认开始点餐
      if (confirm(`${table.tableName}已被预订，是否确认开始点餐？`)) {
        void navigateToNewOrder(table);
      }
    } else if (table.tableStatus && table.tableStatus > 0 && table.tableStatus < 9) {
      // 已点餐状态，跳转到订单详情页面
      router.push({
        path: '/orderDetails',
        query: {
          orderId: table.orderId,
          tableNumber: table.tableId,
          tableName: table.tableName,
          shopId: table.shopId,
          shopName: getShopNameById(table.shopId as number),
        },
      });
    }
  };

  // 添加座位按钮事件
  const handleAddTable = () => {
    showToast('添加新桌位功能将在下一版本推出');
  };

  // 显示提示消息
  const showToast = (message: string) => {
    // 创建toast元素
    const toast = document.createElement('div');
    toast.className = 'toast-message';
    toast.textContent = message;
    toast.style.cssText = `
      position: fixed;
      top: 80px;
      left: 50%;
      transform: translateX(-50%);
      background: rgba(0, 0, 0, 0.8);
      color: white;
      padding: 12px 24px;
      border-radius: 8px;
      z-index: 1000;
      font-size: 16px;
      font-weight: 500;
    `;

    document.body.appendChild(toast);

    setTimeout(() => {
      if (document.body.contains(toast)) {
        document.body.removeChild(toast);
      }
    }, 2000);
  };
</script>
<style scoped>
  .main-bg {
    min-height: 100vh;
    width: 100%;
    background: linear-gradient(135deg, #eff6ff 0%, #f3e8ff 100%);
    position: relative;
  }

  .navbar {
    background: #3b82f6;
    color: #fff;
    padding: 12px 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: sticky;
    top: 0;
    z-index: 10;
  }

  .navbar-title h1 {
    font-size: 24px;
    font-weight: 600;
    margin: 0;
    line-height: 32px;
  }

  .navbar-icons button {
    background: none;
    border: none;
    color: #fff;
    font-size: 22px;
    margin-left: 18px;
    cursor: pointer;
  }

  .refresh-bar {
    background: #2563eb;
    color: #fff;
    padding: 8px 0;
    text-align: center;
    font-size: 20px;
    line-height: 28px;
  }

  .refresh-bar button {
    background: none;
    border: none;
    color: #fff;
    font-weight: 500;
    font-size: inherit;
    cursor: pointer;
  }

  .legend-bar {
    background: #fff;
    padding: 16px;
    font-size: 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .legend-list {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
  }

  .legend-item {
    display: flex;
    align-items: center;
  }

  .legend-dot {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    margin-right: 8px;
  }

  .legend-dot-blue {
    background: #3b82f6;
  }

  .legend-dot-green {
    background: #10b981;
  }

  .legend-dot-yellow {
    background: #f59e0b;
  }

  .legend-dot-gray {
    background: #6b7280;
  }

  .legend-dot-pink {
    background: #ec4899;
  }

  .legend-time {
    display: flex;
    align-items: center;
    font-size: 18px;
    color: #555;
  }

  .legend-time i {
    margin-right: 8px;
  }

  .table-grid-wrap {
    padding: 24px;
  }

  .table-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 20px;
  }

  @media (min-width: 768px) {
    .table-grid {
      /* grid-template-columns: repeat(5, minmax(0, 1fr)); */
      grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    }
  }

  .table-title {
    font-size: 28px;
    font-weight: bold;
    margin-bottom: 12px;
  }

  .table-status {
    margin-bottom: 12px;
  }

  .table-info {
    margin-top: auto;
  }

  .table-info-row {
    display: flex;
    align-items: center;
    margin-bottom: 6px;
    font-size: 16px;
    color: #666;
  }

  .table-info-row i {
    margin-right: 8px;
  }

  .add-table-card {
    background-color: white;
    border: 2px dashed #ddd;
    border-radius: 12px;
    padding: 16px;
    min-height: 120px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
  }

  .add-table-card:hover {
    border-color: #ff5722;
    background-color: #fff8f6;
  }

  .add-table-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    color: #ff9800;
    font-size: 22px;
    font-weight: bold;
  }

  .add-table-content i {
    font-size: 32px;
    margin-bottom: 8px;
  }

  /* 玻璃卡片和桌位卡片样式 */
  .glass-card {
    background: rgba(255, 255, 255, 0.25);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.18);
    box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.15);
  }

  .table-card {
    border-radius: 12px;
    padding: 12px 8px;
    min-height: 160px;
    cursor: pointer;
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }

  .table-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 15px rgba(0, 0, 0, 0.1);
  }

  .table-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0.1));
    border-radius: 12px;
    z-index: -1;
  }

  .status-badge {
    display: inline-block;
    padding: 4px 8px;
    background-color: rgba(255, 255, 255, 0.25);
    border-radius: 20px;
    font-size: 20px;
    backdrop-filter: blur(5px);
    -webkit-backdrop-filter: blur(5px);
  }

  .table-card.available {
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.7), rgba(37, 99, 235, 0.5));
    color: rgb(37, 36, 36);
    border-left: 4px solid #3b82f6;
  }

  .table-card.ordered {
    background: linear-gradient(135deg, rgba(16, 185, 129, 0.7), rgba(5, 150, 105, 0.5));
    color: rgb(3, 7, 248);
    border-left: 4px solid #10b981;
  }

  .table-card.reserved {
    background: linear-gradient(135deg, rgba(245, 158, 11, 0.7), rgba(217, 119, 6, 0.5));
    color: rgb(136, 3, 3);
    border-left: 4px solid #f59e0b;
  }

  .table-card.disabled {
    background: linear-gradient(135deg, rgba(156, 163, 175, 0.7), rgba(107, 114, 128, 0.5));
    color: white;
    opacity: 0.8;
    cursor: not-allowed;
    border-left: 4px solid #6b7280;
  }

  .table-card.cleaning {
    background: linear-gradient(135deg, rgba(236, 72, 153, 0.7), rgba(219, 39, 119, 0.5));
    color: rgb(0, 134, 76);
    border-left: 4px solid #ec4899;
  }

  .restaurant-selector {
    position: relative;
    display: inline-block;
  }

  .restaurant-dropdown {
    background: rgba(255, 255, 255, 0.1);
    border: 2px solid rgba(255, 255, 255, 0.2);
    border-radius: 16px;
    color: #ffffff;
    font-size: 20px;
    font-weight: 700;
    padding: 12px 60px 12px 20px;
    cursor: pointer;
    transition: all 0.3s ease;
    backdrop-filter: blur(10px);
    min-width: 250px;
    width: 250px;
    box-sizing: border-box;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
  }

  .restaurant-dropdown:hover {
    background: rgba(255, 255, 255, 0.15);
    border-color: rgba(255, 255, 255, 0.3);
    transform: translateY(-2px);
  }

  .restaurant-dropdown:focus {
    outline: none;
    border-color: #007aff;
    box-shadow: 0 0 0 3px rgba(0, 122, 255, 0.3);
  }

  .restaurant-selector::after {
    content: '▼';
    position: absolute;
    right: 16px;
    top: 50%;
    transform: translateY(-50%);
    color: #ffffff;
    font-size: 16px;
    pointer-events: none;
    transition: transform 0.3s ease;
  }

  .restaurant-selector:hover::after {
    transform: translateY(-50%) scale(1.1);
  }

  .restaurant-dropdown option {
    background-color: #1c1c1e;
    color: #ffffff;
    padding: 12px;
    font-size: 18px;
    font-weight: 500;
  }

  .restaurant-dropdown option:hover {
    background-color: #007aff;
  }

  /* 导航菜单样式 */
  .nav-dropdown {
    position: relative;
    display: inline-block;
  }

  .nav-menu-btn {
    background: rgba(255, 255, 255, 0.15);
    color: white;
    border: 1px solid rgba(255, 255, 255, 0.3);
    padding: 10px 16px;
    border-radius: 8px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .nav-menu-btn:hover {
    background: rgba(255, 255, 255, 0.25);
    border-color: rgba(255, 255, 255, 0.5);
  }

  .nav-menu-btn:active {
    transform: scale(0.98);
  }

  .nav-menu {
    position: absolute;
    top: 100%;
    left: 0;
    background-color: #ffffff;
    border-radius: 12px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
    border: 1px solid #e5e5ea;
    min-width: 200px;
    z-index: 1000;
    padding: 8px 0;
    margin-top: 8px;
    opacity: 0;
    visibility: hidden;
    transform: translateY(-10px);
    transition: all 0.2s ease;
  }

  .nav-menu.show {
    opacity: 1;
    visibility: visible;
    transform: translateY(0);
  }

  .nav-menu-item {
    display: block;
    width: 100%;
    padding: 12px 16px;
    background: none;
    border: none;
    text-align: left;
    font-size: 16px;
    color: #000000;
    cursor: pointer;
    border-radius: 8px;
    margin: 2px 8px;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 500;
  }

  .nav-menu-item:hover {
    background-color: rgba(59, 130, 246, 0.1);
    color: #3b82f6;
  }

  .nav-menu-item:active {
    transform: scale(0.98);
  }

  .nav-menu-item i {
    width: 16px;
    font-size: 14px;
  }

  /* 确认订单按钮样式 */
  .confirm-order-btn {
    position: absolute;
    top: 8px;
    right: 8px;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: linear-gradient(135deg, #22c55e, #16a34a);
    color: white;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    box-shadow: 0 2px 8px rgba(34, 197, 94, 0.3);
    transition: all 0.3s ease;
    z-index: 10;
  }

  .confirm-order-btn:hover {
    background: linear-gradient(135deg, #16a34a, #15803d);
    transform: scale(1.1);
    box-shadow: 0 4px 12px rgba(34, 197, 94, 0.4);
  }

  .confirm-order-btn:active {
    transform: scale(1.05);
    box-shadow: 0 2px 8px rgba(34, 197, 94, 0.3);
  }

  .confirm-order-btn i {
    transition: transform 0.3s ease;
  }

  .confirm-order-btn:hover i {
    transform: scale(1.2);
  }

  /* 翻台按钮样式 */
  .flip-table-btn {
    position: absolute;
    top: 8px;
    right: 8px;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: linear-gradient(135deg, #f63b3b, #c51dd8);
    color: white;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
    transition: all 0.3s ease;
    z-index: 10;
  }

  .flip-table-btn:hover {
    background: linear-gradient(135deg, #1d4ed8, #1e40af);
    transform: scale(1.1) rotate(180deg);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
  }

  .flip-table-btn:active {
    transform: scale(1.05) rotate(180deg);
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
  }

  .flip-table-btn i {
    transition: transform 0.3s ease;
  }

  .flip-table-btn:hover i {
    transform: rotate(180deg);
  }

  /* 详细信息样式 */
  .table-info-row-occupied {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 6px;
    font-size: 16px;
  }

  .amount-text {
    /* color: #10b981; */
    font-weight: 600;
    font-size: 16px;
  }

  .occupied-info,
  .reserved-info,
  .disabled-info,
  .cleaning-info {
    margin-top: auto;
  }

  /* 模态框样式 */
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
  }

  .modal-content {
    background: white;
    border-radius: 12px;
    padding: 24px;
    max-width: 400px;
    width: 90%;
    margin: 16px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  }

  /* 进度条样式 */
  .progress-bar-container {
    width: 100%;
    height: 8px;
    background-color: #e5e7eb;
    border-radius: 4px;
    overflow: hidden;
    margin: 16px 0;
  }

  .progress-bar-fill {
    height: 100%;
    background-color: #3b82f6;
    border-radius: 4px;
    transition: width 0.3s ease;
  }

  .progress-bar-fill.bg-green-500 {
    background-color: #10b981;
  }

  /* 动画效果 */
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.7;
    }
  }

  .animate-spin {
    animation: spin 1s linear infinite;
  }

  .animate-pulse {
    animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  }

  /* Toast 消息样式 */
  .toast-message {
    position: fixed;
    top: 80px;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(0, 0, 0, 0.8);
    color: white;
    padding: 12px 24px;
    border-radius: 8px;
    z-index: 1000;
    font-size: 16px;
    font-weight: 500;
    animation: fadeInOut 2s ease-in-out;
  }

  @keyframes fadeInOut {
    0% {
      opacity: 0;
      transform: translateX(-50%) translateY(-10px);
    }
    10%,
    90% {
      opacity: 1;
      transform: translateX(-50%) translateY(0);
    }
    100% {
      opacity: 0;
      transform: translateX(-50%) translateY(-10px);
    }
  }

  /* 响应式调整 */
  @media (max-width: 768px) {
    .table-grid {
      grid-template-columns: repeat(2, 1fr);
      gap: 16px;
    }

    .table-card {
      min-height: 140px;
      padding: 10px;
    }

    .table-title {
      font-size: 24px;
    }

    .table-info-row,
    .table-info-row-occupied {
      font-size: 14px;
    }

    .amount-text {
      font-size: 14px;
    }

    .confirm-order-btn,
    .flip-table-btn {
      width: 32px;
      height: 32px;
      font-size: 12px;
    }

    .modal-content {
      padding: 20px;
      margin: 12px;
    }
  }

  @media (max-width: 480px) {
    .table-grid {
      grid-template-columns: 1fr;
    }

    .navbar {
      flex-direction: column;
      gap: 12px;
      padding: 12px;
    }

    .restaurant-dropdown {
      min-width: 200px;
      width: 200px;
    }
  }
</style>
